import React from "react"
import {
  mapValues,
  get,
  compact,
  keyBy,
  flatten,
  difference,
  isEmpty,
  find,
  set,
} from "lodash"
import styled, { css } from "styled-components"
import { Collapse } from "antd"
import Text from "../../components/Text/Text"
import FormGroup from "./FormGroup"
import CheckIcon from "../../Form/Question/types/CheckIcon"

import withFormGroupVisibility from "./withFormGroupVisibility"

const GroupItem = styled(Collapse.Panel)`
  ${props =>
    props.completed &&
    css`
      opacity: 0.8;
    `}
`

const GroupHeader = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

export default withFormGroupVisibility(
  ({
    formData,
    formGroups,
    transformFormId,
    selectForm,
    visibleFormsForFormGroup,
    onAllFormsCompleted,
  }) => {
    const completedFormGroups = mapValues(
      keyBy(formGroups, "name"),
      formGroup => {
        const formGroupFormIds = formGroup.forms
          ? visibleFormsForFormGroup(formGroup.name).map(
              ([formId, storePath]) => storePath
            )
          : []
        // Take all keys from FormData and put
        const keysFromData = compact(
          flatten(
            Object.keys(formData).map(formId =>
              Array.isArray(formData[formId])
                ? new Array(formData[formId].length)
                    .fill(formId)
                    .map(
                      (formId, index) =>
                        get(formData, [formId, index]) && `${formId}.${index}`
                    )
                : formId
            )
          )
        )
        return difference(formGroupFormIds, keysFromData).length > 0
          ? false
          : true
      }
    );

    const areAllFormsCompleted = Object.keys(completedFormGroups)
      .map((key) => completedFormGroups[key])
      .every((completed) => !!completed);

    if (areAllFormsCompleted && onAllFormsCompleted) {
      onAllFormsCompleted();
    }

    const activeFormGroup = formGroups.reduceRight(
      (activeFormGroup, formGroup) => {
        if (!activeFormGroup) {
          return formGroup.name
        }

        return !completedFormGroups[formGroup.name]
          ? formGroup.name
          : activeFormGroup
      },
      null
    )
    const visibleForms = mapValues(keyBy(formGroups, "name"), formGroup =>
      formGroup.forms
        ? visibleFormsForFormGroup(formGroup.name).map(
            ([formId, storePath]) => {
              const form = find(formGroup.forms, { forms: { _id: formId } })
              return !isEmpty(form) && form.forms
                ? set(
                    { ...form, forms: { ...form.forms } },
                    "forms.storePath",
                    storePath
                  )
                : []
            }
          )
        : []
    )

    return (
      <Collapse
        activeKey={
          !completedFormGroups[activeFormGroup] ? activeFormGroup : null
        }
        bordered={false}
        style={{ width: "100%", background: "white" }}
      >
        {formGroups.map(formGroup => (
          <GroupItem
            header={
              <GroupHeader>
                <div>
                  <Text size="large">{formGroup.name}</Text>
                  <Text size="medium">{formGroup.description}</Text>
                </div>
                {completedFormGroups[formGroup.name] && <CheckIcon />}
              </GroupHeader>
            }
            key={formGroup.name}
            showArrow={false}
            completed={completedFormGroups[formGroup.name]}
          >
            <FormGroup
              visibleForms={get(visibleForms, formGroup.name, [])}
              {...formGroup}
              transformFormId={transformFormId}
              formData={formData}
              selectForm={selectForm}
            />
          </GroupItem>
        ))}
      </Collapse>
    )
  }
)
